import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../components/Button';
import Card from '../components/Card';
import TheatricalResults from '../components/TheatricalResults';
import { useGameStore } from '../stores/gameStore';

const Results = () => {
	const { t } = useTranslation();
	const contextGameId = useGameStore((state) => state.gameId);
	const gameResults = useGameStore((state) => state.gameResults);
	const resetGame = useGameStore((state) => state.resetGame);

	const { gameId } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		if (!contextGameId || contextGameId !== gameId || !gameResults) {
			navigate('/');
		}
	}, [contextGameId, gameId, gameResults, navigate]);

	const goToHome = () => {
		resetGame();
		navigate('/');
	};

	if (!gameResults) {
		return (
			<div className='container-game flex items-center justify-center min-h-screen'>
				<Card className='text-center py-8 px-6'>
					<h2 className='text-2xl font-bold mb-4'>{t('common.loadingResultsMessage')}</h2>
					<div className='w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto'></div>
				</Card>
			</div>
		);
	}

	const winner = gameResults.players[0];
	const second = gameResults.players[1] || null;
	const third = gameResults.players[2] || null;

	return (
		<div className='container-game relative min-h-screen overflow-hidden' style={{ background: 'transparent' }}>
			<TheatricalResults>
				<Card className='text-center py-8 px-6 mb-8 shadow-2xl'>
					<h1 className='text-4xl font-bold mb-8 text-primary-600 animate-pulse'>
						{t('resultsScreen.congratulationsMessage', { winnerName: winner?.name || 'Player' })}
					</h1>

					<div className='podium flex justify-center items-end mb-12 mt-8'>
						{second && (
							<div className='podium-item mx-4 flex flex-col items-center'>
								<div className='avatar mb-2 bg-gray-400 rounded-full w-16 h-16 flex items-center justify-center border-2 border-gray-300'>
									<span className='text-white text-2xl'>{t('resultsScreen.podiumSecondPlaceLabel')}</span>
								</div>
								<div className='name text-lg font-medium'>{second.name}</div>
								<div className='score text-xl font-bold'>
									{second.score} {t('common.ptsSuffix')}
								</div>
								<div className='podium-block bg-gray-300 w-24 h-28 rounded-t-lg mt-2'></div>
							</div>
						)}

						{winner && (
							<div className='podium-item mx-4 flex flex-col items-center'>
								<div className='crown text-yellow-500 text-3xl mb-1'>👑</div>
								<div className='avatar mb-2 bg-yellow-400 rounded-full w-16 h-16 flex items-center justify-center border-2 border-yellow-500'>
									<span className='text-white text-2xl'>{t('resultsScreen.podiumFirstPlaceLabel')}</span>
								</div>
								<div className='name text-lg font-medium'>{winner.name}</div>
								<div className='score text-xl font-bold'>
									{winner.score} {t('common.ptsSuffix')}
								</div>
								<div className='podium-block bg-yellow-300 w-24 h-36 rounded-t-lg mt-2'></div>
							</div>
						)}

						{third && (
							<div className='podium-item mx-4 flex flex-col items-center'>
								<div className='avatar mb-2 bg-amber-600 rounded-full w-16 h-16 flex items-center justify-center border-2 border-amber-700'>
									<span className='text-white text-2xl'>{t('resultsScreen.podiumThirdPlaceLabel')}</span>
								</div>
								<div className='name text-lg font-medium'>{third.name}</div>
								<div className='score text-xl font-bold'>
									{third.score} {t('common.ptsSuffix')}
								</div>
								<div className='podium-block bg-amber-500 w-24 h-20 rounded-t-lg mt-2'></div>
							</div>
						)}
					</div>

					<h2 className='text-2xl font-bold mb-4'>{t('resultsScreen.finalRankingTitle')}</h2>
					<table className='w-full mb-8'>
						<thead>
							<tr className='border-b-2 border-gray-200'>
								<th className='py-2'>{t('resultsScreen.positionHeader')}</th>
								<th className='py-2'>{t('resultsScreen.playerHeader')}</th>
								<th className='py-2'>{t('resultsScreen.scoreHeader')}</th>
							</tr>
						</thead>
						<tbody>
							{gameResults.players.map((player, index) => (
								<tr key={player.playerId} className='border-b border-gray-100'>
									<td className='py-2'>{index + 1}</td>
									<td className='py-2'>{player.name}</td>
									<td className='py-2'>{player.score}</td>
								</tr>
							))}
						</tbody>
					</table>

					<Button onClick={goToHome} className='px-6 py-3'>
						{t('common.returnToHomeButton')}
					</Button>
				</Card>
			</TheatricalResults>
		</div>
	);
};

export default Results;
